import React, { useState, useEffect, useRef } from "react";
import { Button, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    isEqual,
    addDays,
    format,
    startOfDay,
    isWithinInterval
} from "date-fns";
import { getTimeSuggestions } from "../modules/ScheduleService";

import {
    DEFAULT_NUMBER_OF_LOOKAHEAD_DAYS,
    MAX_NUMBER_OF_SUGGESTED_DAYS_TO_DISPLAY,
    MAX_NUMBER_OF_SUGGESTED_SLOTS_TO_DISPLAY
} from "../utils/Constants";
import { useAtom } from "jotai";
import { isImperialCountryAtom } from "../atoms/atoms";
import CustomAnimatedSpinner from "../components/CustomAnimatedSpinner";
import { minBy, pickBy, map, uniqBy } from "lodash";
import { isNumeric } from "../utils";
import { SuggestedTime } from "../components/SuggestedTime";
import {
    bookingGeoLocationAtom,
    selectedBookingForRescheduleAtom,
    selectedRescheduleDateTimeAtom,
    suggestionLookAheadStartDateAtom,
    storedSuggestionsAtom,
    calendarDateAtom
} from "../atoms/providerReschedule";
import { mbxUserAtom } from "../atoms/atoms";
import { setRecurringWeekDaysAtom } from "../atoms/recurringSettings";

const SuggestedTimesSection = ({
    SBs,
    handleSelectedCustomDateTime,
    setHasDateOrTimeChanged,
    isOngoing
}) => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const company = mbxUser.company;
    const providerId = mbxUser.provider.id;
    const providerMaxTravelTime = mbxUser.provider.maxTravelTime;
    const providerTimezone = mbxUser.provider.timezone;
    const [booking] = useAtom(selectedBookingForRescheduleAtom);
    const service = booking.servicetype;
    const bookingLocationTimeZone = booking.timezone;
    const [bookingGeoLocation] = useAtom(bookingGeoLocationAtom);
    const [calendarDate] = useAtom(calendarDateAtom);
    const [suggestionLookAheadStartDate] = useAtom(
        suggestionLookAheadStartDateAtom
    );
    const [, setSelectedRescheduleDateTime] = useAtom(
        selectedRescheduleDateTimeAtom
    );
    const [, setRecurringWeekDays] = useAtom(setRecurringWeekDaysAtom);
    const [selectedSuggestedSlot, setSelectedSuggestedSlot] = useState();
    const [suggestions, setSuggestions] = useState();
    const [storedSuggestions, setStoredSuggestions] = useAtom(
        storedSuggestionsAtom
    );
    const fetchedDays = useRef(new Set());
    const [loading, setLoading] = useState(false);
    const [isImperialCountry] = useAtom(isImperialCountryAtom);
    const suggestedSlotsForDisplay = storedSuggestions?.slots
        ? addLeafFlagToSlots(storedSuggestions.slots)
        : suggestions?.slots
          ? addLeafFlagToSlots(suggestions.slots)
          : [];

    useEffect(() => {
        function uniqueSuggestions(fetchedSuggestions) {
            if (suggestions && fetchedSuggestions) {
                const uniqueSls = uniqBy(
                    [...suggestions.slots, ...fetchedSuggestions.slots],
                    "clusteredReadableSlot.datetime"
                );
                const uniqueFDs = uniqBy(
                    [...suggestions.fullDays, ...fetchedSuggestions.fullDays],
                    "date"
                );

                return {
                    slots: uniqueSls,
                    fullDays: uniqueFDs
                };
            } else return fetchedSuggestions;
        }
        function getFetchDataDateRange() {
            const fetchDataRange = {};
            if (calendarDate)
                for (
                    let lookAhead = 0;
                    lookAhead < DEFAULT_NUMBER_OF_LOOKAHEAD_DAYS;
                    lookAhead++
                ) {
                    const fetchedDay = addDays(calendarDate, lookAhead);

                    if (
                        !fetchedDays.current.has(
                            format(fetchedDay, "yyyy-MM-dd")
                        )
                    ) {
                        fetchDataRange.startDate = fetchedDay;
                        fetchDataRange.lookAhead =
                            DEFAULT_NUMBER_OF_LOOKAHEAD_DAYS - lookAhead;
                        break;
                    }
                }
            return fetchDataRange;
        }
        async function getSuggestions() {
            try {
                setLoading(true);
                setSelectedSuggestedSlot();
                const fetechDataDateRange = getFetchDataDateRange();
                if (fetechDataDateRange.startDate) {
                    const fetchedSuggestions = await getTimeSuggestions(
                        bookingGeoLocation,
                        SBs,
                        fetechDataDateRange.lookAhead,
                        fetechDataDateRange.startDate,
                        MAX_NUMBER_OF_SUGGESTED_SLOTS_TO_DISPLAY,
                        MAX_NUMBER_OF_SUGGESTED_DAYS_TO_DISPLAY,
                        company,
                        service,
                        providerTimezone, //bookingLocationTimeZone,
                        providerId,
                        providerMaxTravelTime,
                        false
                    );

                    // Only store suggestions if they haven't been fetched yet
                    if (!storedSuggestions) {
                        const uniqueFetchedSuggestions =
                            uniqueSuggestions(fetchedSuggestions);
                        // sort slots
                        if (uniqueFetchedSuggestions.slots.length) {
                            uniqueFetchedSuggestions.slots.sort(
                                (slot1, slot2) => {
                                    const slot1DateTime = new Date(
                                        slot1.clusteredReadableSlot?.datetime
                                    ).valueOf();
                                    const slot2DateTime = new Date(
                                        slot2.clusteredReadableSlot?.datetime
                                    ).valueOf();
                                    if (slot1DateTime > slot2DateTime) return 1;
                                    else if (slot1DateTime < slot2DateTime)
                                        return -1;
                                    else return 0;
                                }
                            );
                        }
                        setSuggestions(uniqueFetchedSuggestions);
                        setStoredSuggestions(uniqueFetchedSuggestions);
                    }

                    for (
                        let lookAhead = 0;
                        lookAhead < fetechDataDateRange.lookAhead;
                        lookAhead++
                    ) {
                        fetchedDays.current.add(
                            format(
                                addDays(
                                    fetechDataDateRange.startDate,
                                    lookAhead
                                ),
                                "yyyy-MM-dd"
                            )
                        );
                    }
                }
            } catch (err) {
                console.log("getSuggestions error", err);
            } finally {
                setLoading(false);
            }
        }

        // Run the fetch function only if there are no stored suggestions
        if (!storedSuggestions && suggestionLookAheadStartDate) {
            getSuggestions();
        }
    }, [
        service,
        company,
        SBs,
        bookingGeoLocation,
        calendarDate,
        storedSuggestions,
        providerId,
        bookingLocationTimeZone,
        providerMaxTravelTime,
        suggestionLookAheadStartDate
    ]);

    function isSelectedSlot(sl) {
        return isEqual(
            new Date(selectedSuggestedSlot?.datetime),
            new Date(sl.clusteredReadableSlot.datetime)
        );
    }

    function displayOnlyWithinDateRange(day) {
        const startDateMom = startOfDay(calendarDate);
        const endDateMom = startOfDay(
            addDays(calendarDate, DEFAULT_NUMBER_OF_LOOKAHEAD_DAYS - 1)
        );
        return isWithinInterval(new Date(day.date), {
            start: startDateMom,
            end: endDateMom
        });
    }
    function onSuggestedSlotSelection(slot) {
        setSelectedSuggestedSlot(slot.clusteredReadableSlot);
        setSelectedRescheduleDateTime(
            new Date(slot.clusteredReadableSlot.datetime)
        );
        setHasDateOrTimeChanged();
        if (isOngoing)
            setRecurringWeekDays(new Date(slot.clusteredReadableSlot.datetime));
    }

    const theme = useTheme();

    return (
        <>
            {loading && (
                <Grid container columnSpacing={2.25} rowSpacing={2.25} mb={2}>
                    <Grid
                        item
                        xs={12}
                        key={"suggestions-loader"}
                        container
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <CustomAnimatedSpinner loading={loading} />
                    </Grid>
                </Grid>
            )}
            {!loading && (
                <Grid
                    container
                    item
                    rowSpacing={2.25}
                    columnSpacing={2.25}
                    sx={{ justifyContent: "center" }}
                >
                    {suggestedSlotsForDisplay
                        ?.filter(displayOnlyWithinDateRange)
                        .map((sl, idx) => {
                            if (idx < 6) {
                                let displayDateTime = new Date(sl.date);
                                displayDateTime.setHours(
                                    sl.clusteredReadableSlot.hr
                                );
                                displayDateTime.setMinutes(
                                    sl.clusteredReadableSlot.mins
                                );
                                sl.displayDateTime = new Date(displayDateTime);
                                return (
                                    <Grid
                                        item
                                        key={idx}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <SuggestedTime
                                            isSelected={isSelectedSlot(sl)}
                                            suggestedTime={displayDateTime}
                                            travelTime={
                                                sl.adjascentBookingTravelTime
                                            }
                                            travelDistance={
                                                isImperialCountry
                                                    ? sl.adjacentBookingDistance /
                                                      1.609344
                                                    : sl.adjacentBookingDistance
                                            }
                                            distanceUnit={
                                                isImperialCountry ? "mi" : "km"
                                            }
                                            onSelect={onSuggestedSlotSelection}
                                            slot={sl}
                                        />
                                    </Grid>
                                );
                            } else return <></>;
                        })}
                    <Grid
                        item
                        key={"button-suggestion-none"}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <Button
                            sx={{
                                height: "14px",
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "unset",
                                    color: theme.palette.primary.dark
                                }
                            }}
                            onClick={() => {
                                setSelectedRescheduleDateTime(null);
                                handleSelectedCustomDateTime();
                            }}
                        >
                            Select custom date and time
                        </Button>
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default SuggestedTimesSection;

const addLeafFlagToSlots = (slots) => {
    let leafIndexes = map(
        // all existing adjascentBookingTravelTimes under 5 min
        Object.keys(
            pickBy(
                // when used on an array it adds numeric keys
                slots,
                ({ adjascentBookingTravelTime }) =>
                    isNumeric(adjascentBookingTravelTime) &&
                    adjascentBookingTravelTime <= 5
            )
        ),
        Number
    );
    // by any existing adjascentBookingTravelTimes
    if (leafIndexes.length === 0) {
        const indexOfMinTravelTimeSlot = slots.indexOf(
            minBy(slots, "adjascentBookingTravelTime")
        );
        indexOfMinTravelTimeSlot !== -1 &&
            leafIndexes.push(indexOfMinTravelTimeSlot);
    }
    // by start time
    if (leafIndexes.length === 0) {
        const indexOfMinStartTime = slots.indexOf(minBy(slots, "date"));
        indexOfMinStartTime !== -1 && leafIndexes.push(indexOfMinStartTime);
    }
    return slots.map((slot, index) => ({
        ...slot,
        showLeafNoteTravelTime: slot.adjascentBookingTravelTime ?? 0,
        showLeafNote: leafIndexes.includes(index)
    }));
};
